import React, {useEffect, useState} from 'react';
import UTIF from 'utif2';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {ArrowForward} from '@mui/icons-material';
import {Box, Typography} from '@mui/material';
import ComponentLoader from '@components/loaders/component-loader/ComponentLoader';
import {
  IconButtonWrapper,
  NavigationButtonsTiff,
  TextContainer
} from './TiffViewerStyles';
import {t} from 'i18next';
import HighlighterWrapper from '@components/highlighterWrapper/HighlighterWrapper';
import EncryptUtils from 'src/utils/EncryptUtils';
import { SEARCH_CONTEXT } from 'src/routes/BaseRoute';
import { useLocation } from 'react-router-dom';

interface Page {
  id: number;
  number: number;
}

interface TiffViewerProps {
  tiffBuffer: Buffer | ArrayBuffer;
}

const toArrayBuffer = (buffer: Buffer | ArrayBuffer) =>
  buffer instanceof ArrayBuffer
    ? buffer
    : buffer.buffer.slice(
        buffer.byteOffset,
        buffer.byteOffset + buffer.byteLength
      ) as ArrayBuffer;

const TiffViewer: React.FC<TiffViewerProps> = ({tiffBuffer}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [pages, setPages] = useState<Page[]>([]);
  const [pageNumber, setPageNumber] = useState(0);
  const [image, setImage] = useState<HTMLImageElement | undefined>();
  const [error, setError] = useState<string | null>(null);

  const location = useLocation();

  const search_context = EncryptUtils.getDecryptedData(
    location?.search,
    SEARCH_CONTEXT
  );

  useEffect(() => {
    try {
      const ifds = UTIF.decode(tiffBuffer);
      const pages = ifds.map((page, i) => ({id: i, number: i + 1}));
      setPages(pages);
    } catch (error) {
      console.error('Error decoding TIFF:', error);
      setError('Error decoding TIFF');
    }
  }, [tiffBuffer]);

  useEffect(() => {
    setIsLoading(true);
    try {
      const ifds = UTIF.decode(tiffBuffer);
      const pageOfTiff = ifds[pageNumber];
      UTIF.decodeImage(tiffBuffer, pageOfTiff);
      const rgba = UTIF.toRGBA8(pageOfTiff);

      const imageWidth = pageOfTiff.width;
      const imageHeight = pageOfTiff.height;

      const cnv = document.createElement('canvas');
      cnv.width = imageWidth;
      cnv.height = imageHeight;

      const ctx = cnv.getContext('2d', {alpha: false})!;
      const imageData = ctx.createImageData(imageWidth, imageHeight);
      imageData.data.set(rgba);
      ctx.putImageData(imageData, 0, 0);

      // Use the 'load' event to ensure the image is fully loaded before accessing its properties
      const img = new Image();
      img.onload = () => {
        setImage(img);
        setIsLoading(false);
      };
      img.src = cnv.toDataURL(); // Convert canvas to data URL and set it as the source
    } catch (error) {
      try {
        const blob = new Blob([toArrayBuffer(tiffBuffer)], {type: 'image/jpeg'});
        const imageUrl = URL.createObjectURL(blob);

        const img = new Image();
        img.onload = () => {
          setImage(img);
          setIsLoading(false);
        };
        img.src = imageUrl;
        setIsLoading(false);
      } catch (error) {
        console.error('Error processing TIFF image:', error);
        setError('Error processing TIFF image');
        setIsLoading(false);
      }
    }
  }, [pageNumber, tiffBuffer]);

  const handlePageChange = async (newPageNumber: number) => {
    try {
      setPageNumber(newPageNumber);
    } catch (error) {
      console.error('Error changing page:', error);
      setError('Error changing page');
      setIsLoading(false);
    }
  };

  return (
    <div>
      <HighlighterWrapper keywords={[search_context]}>
        {error ? (
          <TextContainer>
            <Typography variant="h6">
              {t('PRIVATE.DOCUMENT.NO_PREVIEW_AVAILABLE', {
                ns: 'private'
              })}
            </Typography>
          </TextContainer>
        ) : (
          <>
            {pages?.length > 1 ? (
              <NavigationButtonsTiff>
                <IconButtonWrapper
                  type="button"
                  disabled={pageNumber == 0}
                  onClick={() => handlePageChange(Math.max(pageNumber - 1, 0))}>
                  <ArrowBackIcon />
                </IconButtonWrapper>
                {`Page ${pageNumber + 1}`}
                <IconButtonWrapper
                  type="button"
                  disabled={pageNumber + 1 == pages?.length}
                  onClick={() =>
                    handlePageChange(Math.min(pageNumber + 1, pages.length - 1))
                  }>
                  <ArrowForward />
                </IconButtonWrapper>
              </NavigationButtonsTiff>
            ) : null}
            <Box>
              {isLoading ? (
                <ComponentLoader />
              ) : (
                <img
                  src={image?.src}
                  alt="TIFF"
                  loading="eager"
                  style={{
                    width: `${image?.naturalWidth}px`,
                    height:
                      image?.naturalHeight && image.naturalHeight > 1500
                        ? 'auto' // let it scale naturally with width
                        : `${image?.naturalHeight}px`,
                    maxWidth: '45vw',
                    maxHeight: '95vh', // prevent overflow on very tall images
                    imageRendering: 'crisp-edges'
                  }}
                />
              )}
            </Box>
          </>
        )}
      </HighlighterWrapper>
    </div>
  );
};

export default TiffViewer;
